import * as THREE from "three";

// Map02's authored places: the route phases made physical. Everything is
// procedural massing in an adobe/hesco palette — from gunship altitude,
// valley architecture is flat-roofed boxes inside walled courtyards, which
// primitives render more faithfully than any imported Western kit.
//
// All meshes are plain (no ignoreBallistics): they take hits, occlude LOS,
// and feed the navmesh, so compounds are real cover and real obstacles.

type HeightFn = (x: number, z: number) => number;
type Rng = { next: () => number };

function seededRng(seed: number): Rng {
  let s = seed | 0;
  return {
    next(): number {
      s = Math.imul(s, 1664525) + 1013904223 | 0;
      return (s >>> 0) / 0xffffffff;
    },
  };
}

function rr(rng: Rng, lo: number, hi: number): number {
  return lo + rng.next() * (hi - lo);
}

const ADOBE = new THREE.MeshStandardMaterial({ color: 0xb29a74, roughness: 0.96, metalness: 0.02 });
const ADOBE_DARK = new THREE.MeshStandardMaterial({ color: 0x93805f, roughness: 0.97, metalness: 0.02 });
const ROOF_FLAT = new THREE.MeshStandardMaterial({ color: 0x9c8a68, roughness: 0.94, metalness: 0.02 });
const HESCO = new THREE.MeshStandardMaterial({ color: 0x8f8871, roughness: 0.98, metalness: 0.02 });
const CONCRETE = new THREE.MeshStandardMaterial({ color: 0x8d8a82, roughness: 0.85, metalness: 0.04 });
const METAL = new THREE.MeshStandardMaterial({ color: 0x5d6166, roughness: 0.45, metalness: 0.5 });
const MARKING = new THREE.MeshStandardMaterial({ color: 0xd8d5c8, roughness: 0.9, metalness: 0.0 });
const ROCK_DARK = new THREE.MeshStandardMaterial({ color: 0x574f42, roughness: 0.98, metalness: 0.01 });
const CAVE_VOID = new THREE.MeshBasicMaterial({ color: 0x050505 });

function addBox(
  group: THREE.Group,
  material: THREE.Material,
  w: number,
  h: number,
  d: number,
  x: number,
  y: number,
  z: number,
  rotY = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  mesh.rotation.y = rotY;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return mesh;
}

// Flat-roofed adobe house with a parapet lip — the massing unit everything
// else composes.
function addAdobeHouse(group: THREE.Group, rng: Rng, x: number, z: number, getHeight: HeightFn, rotY = 0, big = false): void {
  const w = big ? rr(rng, 9, 13) : rr(rng, 5.5, 8.5);
  const d = big ? rr(rng, 7, 10) : rr(rng, 5, 7.5);
  const h = rr(rng, 3.0, big ? 5.2 : 4.0);
  const y = getHeight(x, z);
  addBox(group, rng.next() > 0.35 ? ADOBE : ADOBE_DARK, w, h, d, x, y + h / 2, z, rotY);
  // Parapet lip reads as the classic flat roof from above
  addBox(group, ROOF_FLAT, w - 0.7, 0.35, d - 0.7, x, y + h + 0.05, z, rotY);
  addBox(group, ADOBE_DARK, w + 0.35, 0.55, 0.5, x, y + h + 0.28, z + (d / 2) * Math.cos(rotY), rotY);
  if (rng.next() > 0.55) {
    // Second storey step
    const sw = w * 0.45;
    const sh = rr(rng, 1.8, 2.6);
    addBox(group, ADOBE, sw, sh, d * 0.5, x + w * 0.18, y + h + sh / 2, z - d * 0.12, rotY);
  }
}

// Walled courtyard compound: perimeter with a gate gap + houses inside.
export function addCompound(group: THREE.Group, rng: Rng, x: number, z: number, size: number, getHeight: HeightFn, rotY = 0): void {
  const half = size / 2;
  const wallH = rr(rng, 2.2, 2.9);
  const cos = Math.cos(rotY);
  const sin = Math.sin(rotY);
  // Matches three.js rotation.y sense — the opposite convention scissors the
  // walls against their own rotated boxes.
  const at = (lx: number, lz: number): [number, number] => [x + lx * cos + lz * sin, z - lx * sin + lz * cos];

  // Perimeter: south wall in two segments with a gate gap, then north/east/west runs
  const gate = size * 0.16;
  for (const side of [-1, 1]) {
    const segLen = (size - gate) / 2;
    const [sx, sz] = at(side * (gate / 2 + segLen / 2), -half);
    addBox(group, ADOBE_DARK, segLen, wallH, 0.6, sx, getHeight(sx, sz) + wallH / 2, sz, rotY);
  }
  const [nx, nz] = at(0, half);
  addBox(group, ADOBE_DARK, size, wallH, 0.6, nx, getHeight(nx, nz) + wallH / 2, nz, rotY);
  for (const side of [-1, 1]) {
    const [ex, ez] = at(side * half, 0);
    addBox(group, ADOBE_DARK, 0.6, wallH, size, ex, getHeight(ex, ez) + wallH / 2, ez, rotY);
  }

  const houses = 1 + Math.floor(rng.next() * 2.4);
  for (let i = 0; i < houses; i++) {
    const [hx, hz] = at(rr(rng, -half * 0.45, half * 0.45), rr(rng, -half * 0.3, half * 0.55));
    addAdobeHouse(group, rng, hx, hz, getHeight, rotY + rr(rng, -0.12, 0.12), i === 0);
  }
  if (rng.next() > 0.6) {
    const [tx, tz] = at(half * 0.8, half * 0.8);
    const ty = getHeight(tx, tz);
    addBox(group, ADOBE, 1.6, 5.4, 1.6, tx, ty + 2.7, tz, rotY); // corner watchtower
    addBox(group, ROOF_FLAT, 2.3, 0.3, 2.3, tx, ty + 5.6, tz, rotY);
  }
}

// True where a building footprint may sit — the map supplies this to keep
// structures off the road corridor and out of the river channel.
export type PlacementClear = (x: number, z: number) => boolean;

// Loose cluster of compounds and standalone houses along the route.
export function addHamlet(group: THREE.Group, seed: number, cx: number, cz: number, spread: number, count: number, getHeight: HeightFn, clear: PlacementClear): void {
  const rng = seededRng(seed);
  let placed = 0;
  for (let attempt = 0; attempt < count * 4 && placed < count; attempt++) {
    const angle = rng.next() * Math.PI * 2;
    const radius = Math.sqrt(rng.next()) * spread;
    const x = cx + Math.cos(angle) * radius;
    const z = cz + Math.sin(angle) * radius;
    if (!clear(x, z)) continue;
    const rotY = rr(rng, -0.4, 0.4);
    if (rng.next() > 0.45) addCompound(group, rng, x, z, rr(rng, 16, 24), getHeight, rotY);
    else addAdobeHouse(group, rng, x, z, getHeight, rotY);
    placed++;
  }
}

// Denser valley town: irregular rows of compounds with alley gaps.
export function addLowTown(group: THREE.Group, seed: number, cx: number, cz: number, getHeight: HeightFn, clear: PlacementClear): void {
  const rng = seededRng(seed);
  const rowAxis = 0.55; // roughly aligned with the route through town
  for (let row = -2; row <= 2; row++) {
    for (let col = -2; col <= 2; col++) {
      if (rng.next() < 0.18) continue; // alley/served gap
      const lx = col * 30 + rr(rng, -5, 5);
      const lz = row * 26 + rr(rng, -4, 4);
      const x = cx + lx * Math.cos(rowAxis) + lz * Math.sin(rowAxis);
      const z = cz - lx * Math.sin(rowAxis) + lz * Math.cos(rowAxis);
      if (!clear(x, z)) continue;
      if (rng.next() > 0.3) addCompound(group, rng, x, z, rr(rng, 15, 22), getHeight, rowAxis + rr(rng, -0.15, 0.15));
      else addAdobeHouse(group, rng, x, z, getHeight, rowAxis + rr(rng, -0.3, 0.3), rng.next() > 0.5);
    }
  }
}

// Fortified friendly base: hesco perimeter, gate chicane, barracks rows,
// corner towers, staging yard.
export function addForwardBase(group: THREE.Group, cx: number, cz: number, getHeight: HeightFn): void {
  const rng = seededRng(0xba5e);
  const w = 96;
  const d = 74;
  const hescoH = 2.4;
  const seg = 8;
  // Perimeter hesco runs with a north gate gap (the route leaves that way —
  // the squad files out through the gate once the navmesh is up)
  for (let x = -w / 2; x < w / 2; x += seg) {
    for (const side of [-1, 1]) {
      if (side === 1 && Math.abs(x + seg / 2 - 14) < 10) continue; // gate gap (north-east)
      const px = cx + x + seg / 2;
      const pz = cz + side * (d / 2);
      addBox(group, HESCO, seg - 0.6, hescoH, 2.2, px, getHeight(px, pz) + hescoH / 2, pz);
    }
  }
  for (let z = -d / 2; z < d / 2; z += seg) {
    for (const side of [-1, 1]) {
      const px = cx + side * (w / 2);
      const pz = cz + z + seg / 2;
      addBox(group, HESCO, 2.2, hescoH, seg - 0.6, px, getHeight(px, pz) + hescoH / 2, pz);
    }
  }
  // Gate chicane barriers outside the north gate
  addBox(group, CONCRETE, 6, 1.2, 1.2, cx + 9, getHeight(cx + 9, cz + d / 2 + 5) + 0.6, cz + d / 2 + 5);
  addBox(group, CONCRETE, 6, 1.2, 1.2, cx + 19, getHeight(cx + 19, cz + d / 2 + 9) + 0.6, cz + d / 2 + 9);
  // Corner towers
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const tx = cx + sx * (w / 2 - 3);
    const tz = cz + sz * (d / 2 - 3);
    const ty = getHeight(tx, tz);
    addBox(group, HESCO, 3, 5.6, 3, tx, ty + 2.8, tz);
    addBox(group, METAL, 4, 0.3, 4, tx, ty + 5.9, tz);
  }
  // Barracks / container rows
  for (let i = 0; i < 4; i++) {
    const bx = cx - w / 2 + 16 + i * 18;
    const bz = cz + d / 2 - 16;
    addBox(group, METAL, 12, 3.2, 5.5, bx, getHeight(bx, bz) + 1.6, bz);
  }
  for (let i = 0; i < 3; i++) {
    const bx = cx - w / 2 + 14 + i * 9;
    const bz = cz - 8 + rr(rng, -2, 2);
    addBox(group, CONCRETE, 6, 2.6, 6, bx, getHeight(bx, bz) + 1.3, bz);
  }
}

// Real bridge across the main channel: deck, piers, rails, approach ramps.
// The deck rasterizes into the navmesh, so it's the only crossing.
export function addBridge(group: THREE.Group, centerX: number, z: number, span: number, getHeight: HeightFn): void {
  const deckY = 2.0;
  const deckW = 11;
  const deck = addBox(group, CONCRETE, span, 0.8, deckW, centerX, deckY, z);
  deck.name = "map02-bridge-deck";
  // Piers into the bed
  for (const off of [-span * 0.25, 0, span * 0.25]) {
    const px = centerX + off;
    const bed = getHeight(px, z);
    addBox(group, CONCRETE, 2.2, deckY - bed + 0.4, 2.2, px, bed + (deckY - bed) / 2, z);
  }
  // Rails
  for (const side of [-1, 1]) {
    addBox(group, METAL, span, 1.0, 0.3, centerX, deckY + 0.9, z + side * (deckW / 2 - 0.3));
  }
  // Approach ramps to grade
  for (const side of [-1, 1]) {
    const ax = centerX + side * (span / 2 + 7);
    const ay = getHeight(ax, z);
    const ramp = addBox(group, CONCRETE, 16, 0.8, deckW, ax, (deckY + ay) / 2 + 0.2, z);
    ramp.rotation.z = side * Math.atan2(deckY - ay, 16) * -1;
  }
}

// Roadside checkpoint: hesco pocket, barrier arm, hut.
export function addCheckpoint(group: THREE.Group, x: number, z: number, rotY: number, getHeight: HeightFn): void {
  const cos = Math.cos(rotY);
  const sin = Math.sin(rotY);
  const at = (lx: number, lz: number): [number, number] => [x + lx * cos + lz * sin, z - lx * sin + lz * cos];
  for (const [lx, lz, w, d] of [[-7, 0, 2, 10], [7, 0, 2, 10], [0, 5.5, 12, 2]] as const) {
    const [px, pz] = at(lx, lz);
    addBox(group, HESCO, w, 2.2, d, px, getHeight(px, pz) + 1.1, pz, rotY);
  }
  const [hx, hz] = at(4, 3);
  addBox(group, ADOBE, 4, 2.8, 3.5, hx, getHeight(hx, hz) + 1.4, hz, rotY);
  const [bx, bz] = at(-2, -1);
  addBox(group, MARKING, 7, 0.18, 0.3, bx, getHeight(bx, bz) + 1.1, bz, rotY + 0.1); // barrier arm
}

// Cave mouth in a slope: rock lintel arc around a black void.
export function addCaveMouth(group: THREE.Group, x: number, z: number, facing: number, getHeight: HeightFn): void {
  const y = getHeight(x, z);
  const cos = Math.cos(facing);
  const sin = Math.sin(facing);
  addBox(group, CAVE_VOID, 5.5, 4.2, 1.2, x, y + 1.6, z, facing);
  for (const [lx, ly, w, h] of [[-3.4, 1.8, 2.4, 5.4], [3.4, 1.8, 2.4, 5.4], [0, 4.6, 8.6, 2.2]] as const) {
    addBox(group, ROCK_DARK, w, h, 3.0, x + lx * cos, y + ly, z + lx * sin, facing + (lx === 0 ? 0 : lx * 0.04));
  }
}

// Exfil hardstand: concrete pad, H marking, perimeter posts, crates.
export function addExfilPad(group: THREE.Group, x: number, z: number, getHeight: HeightFn): void {
  const y = getHeight(x, z);
  addBox(group, CONCRETE, 46, 0.5, 46, x, y + 0.25, z);
  // H marking
  addBox(group, MARKING, 2.2, 0.12, 16, x - 5, y + 0.56, z);
  addBox(group, MARKING, 2.2, 0.12, 16, x + 5, y + 0.56, z);
  addBox(group, MARKING, 8, 0.12, 2.2, x, y + 0.56, z);
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const px = x + Math.cos(angle) * 21;
    const pz = z + Math.sin(angle) * 21;
    addBox(group, METAL, 0.5, 1.6, 0.5, px, y + 0.8, pz);
  }
  const rng = seededRng(0xe8f1);
  for (let i = 0; i < 4; i++) {
    const px = x + rr(rng, -30, -20);
    const pz = z + rr(rng, -18, 18);
    addBox(group, ADOBE_DARK, rr(rng, 2, 3.4), rr(rng, 1.4, 2.2), rr(rng, 2, 3), px, getHeight(px, pz) + 1, pz, rng.next());
  }
}

// Everything placed for the vertical slice, keyed to the mission's route and
// anchors (see missions/river-valley-01.json).
export function addMap02Places(group: THREE.Group, getHeight: HeightFn, clear: PlacementClear): void {
  const places = new THREE.Group();
  places.name = "map02-places";

  addForwardBase(places, -640, -430, getHeight); // BASE GATE — squad starts inside the wire
  addCheckpoint(places, -505, -172, 0.62, getHeight); // route between VALLEY ENTRY and RIVER TRACK
  addHamlet(places, 0x4a3d1, -350, -25, 55, 7, getHeight, clear); // river-hamlet-compound anchor lives here
  addBridge(places, -199, 40, 84, getHeight); // the fork crossing — bridge-north-nest guards it
  addLowTown(places, 0x7041, -160, 210, getHeight, clear); // LOW TOWN + town-block-garrison
  addExfilPad(places, -130, 350, getHeight); // EXFIL LOT finale
  // Cave mouths on the valley walls at the ambush anchors
  addCaveMouth(places, -345, -320, 2.2, getHeight); // valley-entry-ridge anchor
  addCaveMouth(places, -390, -305, 1.9, getHeight);

  group.add(places);
}
